import {css} from "@panda/css";
import {isFuture, parseISO} from "date-fns";

import {isEmpty} from "@pg-mono/nodash";
import {rpAppLink} from "@pg-mono/rp-routes";
import {dateTimeFormat, formatDate, priceFormat} from "@pg-mono/string-utils";
import {pluralize} from "@pg-mono/string-utils";

import {ResponsiveTable} from "../../../atoms/ResponsiveTable";
import {ResponsiveTableRow} from "../../../atoms/ResponsiveTableRow";
import {InvestmentOfferTypeSlug} from "../../../investment_offer/constants/investment_offer_constants";
import {IParkingPlace} from "../../../property/interfaces/IParkingPlace";
import {DetailSectionWrapper} from "../../atoms/DetailSectionWrapper";
import {IOfferDetail} from "../../types/IOfferDetail";
import {OfferType} from "../../types/OfferType";
import {getParkingPlacesType} from "../constants/ParkingPlacesType";
import {DetailViewHeader} from "./DetailViewHeader";
import {OfferDetailFacilities} from "./OfferDetailFacilities";
import {OfferDetailNaturalSites} from "./OfferNaturalSites";
import {OfferStandardDescription} from "./OfferStandardDescription";

interface IProps {
    className?: string;
    offer: IOfferDetail;
    variables: {
        offer_facilities: IValueLabel[];
        offer_natural_sites: IValueLabel[];
        offer_standard_description_type: IValueLabel[];
    } | null;
    showAmenities: boolean;
}

interface IValueLabel {
    label: string;
    value: number;
}

export interface IStandardDescription {
    balconies: string | null;
    bathroom_equipment: string | null;
    building_structure: string | null;
    ceilings: string | null;
    description: string | null;
    exterior_doors: string | null;
    exterior_sills: string | null;
    facade: string | null;
    flooring: string | null;
    heating: string | null;
    installations: string | null;
    interior_sills: string | null;
    kitchen_equipment: string | null;
    roof: string | null;
    types: number[];
    ventilation: string | null;
    walls: string | null;
    windows: string | null;
}

export const OfferDetailDetails = (props: IProps) => {
    const {offer, variables, showAmenities} = props;

    const isApartment = offer.is_holiday_location || offer.is_condohotel;
    const futureEndDate = isFuture(parseISO(offer.construction_date_range.upper));
    const upper = formatDate(offer.construction_date_range.upper, dateTimeFormat.quarter);
    const lower = formatDate(offer.construction_date_range.lower, dateTimeFormat.quarter);
    const buildingsPlural = pluralize(["budynku", "budynków", "budynków"]);
    const buildingsCommercialPlural = pluralize(["budynku", "budynków", "budynków"]);
    const buildingOwnPlural = pluralize(["budynek posiada", "budynki posiadają", "budynki posiadają"]);
    const buildingNotOwnPlural = pluralize(["nie posiada", "nie posiadają", "nie posiadają"]);
    const housePlural = pluralize(["dom", "domów", "domów"]);
    const localsPlural = pluralize(["lokalu mieszkalnego", "lokali mieszkalnych", "lokali mieszkalnych"]);
    const apartmentsPlural = pluralize(["apartamentu", "apartamentów", "apartamentów"]);
    const commercialsPlural = pluralize(["lokalu użytkowego", "lokali użytkowych", "lokali użytkowych"]);
    const floorsAboveLower = offer.floors_above_ground_range && offer.floors_above_ground_range.lower;
    const floorsAboveUpper = offer.floors_above_ground_range && offer.floors_above_ground_range.upper;
    const floorsAboveGroundPlural = pluralize(["kondygnację naziemną", "kondygnacje naziemne", "kondygnacji naziemnych"]);
    const floorsAboveGroundHousePlural = pluralize(["kondygnację", "kondygnacje", "kondygnacji"]);
    const floorsAboveGroundRangePlural = pluralize(["kondygnacji naziemnej", "kondygnacji naziemnych", "kondygnacji naziemnych"]);
    const floorsBelowGround = offer.floors_below_ground;
    const floorsBelowGroundPlural = pluralize(["kondygnację podziemną", "kondygnacje podziemne", "kondygnacji podziemnych"]);
    const hasAttic = offer.stats?.has_attic;
    const hasEntresol = offer.stats?.has_entresol;
    const parkingPlacesQuantity = pluralize(["miejsce", "miejsca", "miejsc"]);
    const isInvestmentApartment = offer.slogan && offer.slogan === "Apartamenty inwestycyjne";
    const additionalAreasText =
        offer.stats?.additional_areas && `${offer.stats.additional_areas.map((additionalAreas: string) => " " + additionalAreas.toLowerCase())}`;

    const renderParkingPlacesList = () => {
        return (
            <ul className={parkingPlaceListStyle}>
                {offer.parking_places.map((parkingPlace: IParkingPlace) => {
                    return (
                        <li key={parkingPlace.id}>
                            {`${getParkingPlacesType(parkingPlace.type).toLowerCase()} ${
                                parkingPlace.quantity ? ` (${parkingPlace.quantity} ${parkingPlacesQuantity(parkingPlace.quantity)})` : ""
                            } ${!!parkingPlace.price ? ` w cenie ${priceFormat(parkingPlace.price)}` : ""} ${
                                parkingPlace.description ? `: ${parkingPlace.description}` : ""
                            }`}
                        </li>
                    );
                })}
            </ul>
        );
    };

    const renderParkingPlacesHouse = (hasGarages: boolean) => {
        return (
            <>
                <span> {hasGarages ? "oraz" : "natomiast"} przewidziano:</span>
                {renderParkingPlacesList()}
            </>
        );
    };

    const renderParkingPlacesFlat = () => {
        return (
            <>
                <span>w inwestycji przewidziano:</span>
                {renderParkingPlacesList()}
            </>
        );
    };

    const renderHouseParkingText = () => {
        return (
            <>
                {offer.stats?.ranges_garages_count ? "domy posiadają garaże" : "domy nie posiadają garaży"}{" "}
                {offer.parking_places.length ? renderParkingPlacesHouse(!!offer.stats?.ranges_garages_count) : ""}
            </>
        );
    };

    const renderStandardDescription = () => {
        const hasDescription = () => Object.values(offer.standard_description || {}).filter((field) => isEmpty(field));
        const emptyStandard = <ResponsiveTableRow title="Standard wykończenia:" text="informacja u dewelopera" />;

        return offer.standard_description && hasDescription() ? (
            <OfferStandardDescription
                types={variables != null ? variables.offer_standard_description_type : []}
                standardDescription={offer.standard_description}
            />
        ) : (
            emptyStandard
        );
    };

    const renderHolidayCondoText = (
        <>
            {offer.is_holiday_location ? (
                <a href={rpAppLink.investmentOffer.investmentCategory.base({category: InvestmentOfferTypeSlug.HOLIDAY})} target="_blank">
                    Apartament wakacyjny
                </a>
            ) : (
                ""
            )}{" "}
            {offer.is_condohotel ? (
                <a href={rpAppLink.investmentOffer.investmentCategory.base({category: InvestmentOfferTypeSlug.CONDOHOTEL})} target="_blank">
                    Condohotel
                </a>
            ) : (
                ""
            )}
        </>
    );

    return (
        <DetailSectionWrapper>
            <DetailViewHeader title="Szczegóły inwestycji" />

            <ResponsiveTable>
                <ResponsiveTableRow
                    title="Realizacja inwestycji:"
                    text={
                        futureEndDate || !upper
                            ? `${offer.construction_date_range.lower && `rozpoczęcie budowy w ${lower} roku,`}
                                ${offer.construction_date_range.upper ? `planowane oddanie do użytkowania w ${upper} roku` : "inwestycja w trakcie budowy"}`
                            : `${offer.construction_date_range.lower && `rozpoczęcie budowy w ${lower} roku,`} inwestycja została oddana do użytkowania${
                                  offer.construction_date_range.upper ? ` w ${upper} roku` : ""
                              }`
                    }
                />

                {offer.type === OfferType.FLAT && (
                    <ResponsiveTableRow title="Liczba budynków:" text={`cała inwestycja składa się z ${offer.buildings} ${buildingsPlural(offer.buildings)}`} />
                )}

                {offer.type === OfferType.HOUSE && offer.properties && (
                    <ResponsiveTableRow title="Liczba domów:" text={`cała inwestycja składa się z ${offer.properties} ${housePlural(offer.properties)}`} />
                )}

                {offer.type === OfferType.COMMERCIAL && (
                    <ResponsiveTableRow
                        title="Liczba budynków:"
                        text={`cała inwestycja składa się z ${offer.buildings} ${buildingsCommercialPlural(offer.buildings)}`}
                    />
                )}

                {offer.floors_above_ground_range && floorsAboveLower != null && floorsAboveUpper != null && (
                    <ResponsiveTableRow
                        title="Liczba kondygnacji:"
                        text={
                            offer.type === OfferType.FLAT || offer.type === OfferType.COMMERCIAL
                                ? `${buildingOwnPlural(offer.buildings)} ${
                                      floorsAboveLower === floorsAboveUpper
                                          ? `${floorsAboveUpper} ${floorsAboveGroundPlural(floorsAboveUpper)}`
                                          : `od ${floorsAboveLower} do ${floorsAboveUpper} ${floorsAboveGroundRangePlural(floorsAboveUpper)}`
                                  } wraz z parterem oraz ${
                                      floorsBelowGround
                                          ? `${floorsBelowGround} ${floorsBelowGroundPlural(floorsBelowGround)}`
                                          : `${buildingNotOwnPlural(offer.buildings)} kondygnacji podziemnych`
                                  }`
                                : offer.type === OfferType.HOUSE
                                  ? `domy posiadają ${
                                        floorsAboveLower === floorsAboveUpper
                                            ? `${floorsAboveUpper} ${floorsAboveGroundHousePlural(floorsAboveUpper)}`
                                            : `od ${floorsAboveLower} do ${floorsAboveUpper} kondygnacji`
                                    } wraz z parterem${hasAttic ? " oraz posiadają poddasze" : " oraz nie posiadają poddasza"}`
                                  : ""
                        }
                    />
                )}

                {offer.type === OfferType.FLAT && offer.properties && (
                    <ResponsiveTableRow
                        title={`Liczba ${isApartment || isInvestmentApartment ? "apartamentów" : "mieszkań"}:`}
                        text={`cała inwestycja składa się z ${offer.properties} ${
                            isApartment || isInvestmentApartment ? apartmentsPlural(offer.properties) : localsPlural(offer.properties)
                        }`}
                    />
                )}

                {offer.type === OfferType.COMMERCIAL && offer.properties && (
                    <ResponsiveTableRow
                        title="Liczba lokali:"
                        text={`cała inwestycja składa się z ${offer.properties} ${commercialsPlural(offer.properties)}`}
                    />
                )}

                <ResponsiveTableRow
                    title="Wysokość pomieszczeń:"
                    text={
                        offer.stats?.ranges_height_min
                            ? `${
                                  offer.stats.ranges_height_min === offer.stats.ranges_height_max
                                      ? `na wszystkich kondygnacjach po ${offer.stats.ranges_height_min}`
                                      : `w zależności od kondygnacji od ${offer.stats.ranges_height_min} do ${offer.stats.ranges_height_max}`
                              } cm wysokości`
                            : `informacja u dewelopera`
                    }
                />

                {renderStandardDescription()}

                <ResponsiveTableRow
                    title="Rodzaj własności:"
                    text={
                        offer.stats?.ownership_forms.length
                            ? `${offer.stats.ownership_forms.map((ownership: string) => " " + ownership.toLowerCase())}`
                            : `informacja u dewelopera`
                    }
                />

                {offer.type === OfferType.FLAT && (
                    <>
                        <ResponsiveTableRow
                            title="Powierzchnie dodatkowe:"
                            text={
                                additionalAreasText
                                    ? `${hasEntresol ? "antresola," : ""}${additionalAreasText}`
                                    : `${hasEntresol ? "antresola" : "nie przewidziano powierzchni dodatkowych"}`
                            }
                        />
                        <ResponsiveTableRow
                            title="Komórki lokatorskie:"
                            text={
                                offer.storage
                                    ? `posiada${offer.storage.quantity ? ` (${offer.storage.quantity})` : ""}${
                                          offer.storage.description ? `: ${offer.storage.description}` : ""
                                      }${!!offer.storage.price ? ` Komórki lokatorskie w cenie ${priceFormat(offer.storage.price)}` : ""}`
                                    : `nie przewidziano`
                            }
                        />
                    </>
                )}

                {offer.type === OfferType.HOUSE && <ResponsiveTableRow title="Garaże:" text={renderHouseParkingText()} />}

                {(offer.type === OfferType.FLAT || offer.type === OfferType.COMMERCIAL) && (
                    <ResponsiveTableRow
                        title="Miejsca postojowe:"
                        text={offer.parking_places.length ? renderParkingPlacesFlat() : "w inwestycji nie przewidziano miejsc postojowych"}
                    />
                )}

                {showAmenities && (
                    <ResponsiveTableRow
                        title="Udogodnienia:"
                        text={
                            offer.facilities && offer.facilities.length ? (
                                <OfferDetailFacilities facilities={offer.facilities} facilitiesTypes={variables != null ? variables.offer_facilities : []} />
                            ) : (
                                "informacja u dewelopera"
                            )
                        }
                    />
                )}

                <ResponsiveTableRow
                    title="Przyroda:"
                    text={
                        offer.natural_sites && offer.natural_sites.length ? (
                            <OfferDetailNaturalSites
                                naturalSites={offer.natural_sites}
                                naturalSitesTypes={variables != null ? variables.offer_natural_sites : []}
                            />
                        ) : (
                            `informacja u dewelopera`
                        )
                    }
                />

                {(offer.is_holiday_location || offer.is_condohotel) && <ResponsiveTableRow title="Typ nieruchomości:" text={renderHolidayCondoText} />}
            </ResponsiveTable>
        </DetailSectionWrapper>
    );
};

const parkingPlaceListStyle = css({
    margin: 0,
    py: 0,
    px: 2
});
import {css} from "@panda/css";
import {isFuture, parseISO} from "date-fns";

import {isEmpty} from "@pg-mono/nodash";
import {rpAppLink} from "@pg-mono/rp-routes";
import {dateTimeFormat, formatDate, priceFormat} from "@pg-mono/string-utils";
import {pluralize} from "@pg-mono/string-utils";

import {ResponsiveTable} from "../../../atoms/ResponsiveTable";
import {ResponsiveTableRow} from "../../../atoms/ResponsiveTableRow";
import {InvestmentOfferTypeSlug} from "../../../investment_offer/constants/investment_offer_constants";
import {IParkingPlace} from "../../../property/interfaces/IParkingPlace";
import {DetailSectionWrapper} from "../../atoms/DetailSectionWrapper";
import {IOfferDetail} from "../../types/IOfferDetail";
import {OfferType} from "../../types/OfferType";
import {getParkingPlacesType} from "../constants/ParkingPlacesType";
import {DetailViewHeader} from "./DetailViewHeader";
import {OfferDetailFacilities} from "./OfferDetailFacilities";
import {OfferDetailNaturalSites} from "./OfferNaturalSites";
import {OfferStandardDescription} from "./OfferStandardDescription";

interface IProps {
    className?: string;
    offer: IOfferDetail;
    variables: {
        offer_facilities: IValueLabel[];
        offer_natural_sites: IValueLabel[];
        offer_standard_description_type: IValueLabel[];
    } | null;
    showAmenities: boolean;
}

interface IValueLabel {
    label: string;
    value: number;
}

export interface IStandardDescription {
    balconies: string | null;
    bathroom_equipment: string | null;
    building_structure: string | null;
    ceilings: string | null;
    description: string | null;
    exterior_doors: string | null;
    exterior_sills: string | null;
    facade: string | null;
    flooring: string | null;
    heating: string | null;
    installations: string | null;
    interior_sills: string | null;
    kitchen_equipment: string | null;
    roof: string | null;
    types: number[];
    ventilation: string | null;
    walls: string | null;
    windows: string | null;
}

export const OfferDetailDetails = (props: IProps) => {
    const {offer, variables, showAmenities} = props;

    const isApartment = offer.is_holiday_location || offer.is_condohotel;
    const futureEndDate = isFuture(parseISO(offer.construction_date_range.upper));
    const upper = formatDate(offer.construction_date_range.upper, dateTimeFormat.quarter);
    const lower = formatDate(offer.construction_date_range.lower, dateTimeFormat.quarter);
    const buildingsPlural = pluralize(["budynku", "budynków", "budynków"]);
    const buildingsCommercialPlural = pluralize(["budynku", "budynków", "budynków"]);
    const buildingOwnPlural = pluralize(["budynek posiada", "budynki posiadają", "budynki posiadają"]);
    const buildingNotOwnPlural = pluralize(["nie posiada", "nie posiadają", "nie posiadają"]);
    const housePlural = pluralize(["dom", "domów", "domów"]);
    const localsPlural = pluralize(["lokalu mieszkalnego", "lokali mieszkalnych", "lokali mieszkalnych"]);
    const apartmentsPlural = pluralize(["apartamentu", "apartamentów", "apartamentów"]);
    const commercialsPlural = pluralize(["lokalu użytkowego", "lokali użytkowych", "lokali użytkowych"]);
    const floorsAboveLower = offer.floors_above_ground_range && offer.floors_above_ground_range.lower;
    const floorsAboveUpper = offer.floors_above_ground_range && offer.floors_above_ground_range.upper;
    const floorsAboveGroundPlural = pluralize(["kondygnację naziemną", "kondygnacje naziemne", "kondygnacji naziemnych"]);
    const floorsAboveGroundHousePlural = pluralize(["kondygnację", "kondygnacje", "kondygnacji"]);
    const floorsAboveGroundRangePlural = pluralize(["kondygnacji naziemnej", "kondygnacji naziemnych", "kondygnacji naziemnych"]);
    const floorsBelowGround = offer.floors_below_ground;
    const floorsBelowGroundPlural = pluralize(["kondygnację podziemną", "kondygnacje podziemne", "kondygnacji podziemnych"]);
    const hasAttic = offer.stats?.has_attic;
    const hasEntresol = offer.stats?.has_entresol;
    const parkingPlacesQuantity = pluralize(["miejsce", "miejsca", "miejsc"]);
    const isInvestmentApartment = offer.slogan && offer.slogan === "Apartamenty inwestycyjne";
    const additionalAreasText =
        offer.stats?.additional_areas && `${offer.stats.additional_areas.map((additionalAreas: string) => " " + additionalAreas.toLowerCase())}`;

    const renderParkingPlacesList = () => {
        return (
            <ul className={parkingPlaceListStyle}>
                {offer.parking_places.map((parkingPlace: IParkingPlace) => {
                    return (
                        <li key={parkingPlace.id}>
                            {`${getParkingPlacesType(parkingPlace.type).toLowerCase()} ${
                                parkingPlace.quantity ? ` (${parkingPlace.quantity} ${parkingPlacesQuantity(parkingPlace.quantity)})` : ""
                            } ${!!parkingPlace.price ? ` w cenie ${priceFormat(parkingPlace.price)}` : ""} ${
                                parkingPlace.description ? `: ${parkingPlace.description}` : ""
                            }`}
                        </li>
                    );
                })}
            </ul>
        );
    };

    const renderParkingPlacesHouse = (hasGarages: boolean) => {
        return (
            <>
                <span> {hasGarages ? "oraz" : "natomiast"} przewidziano:</span>
                {renderParkingPlacesList()}
            </>
        );
    };

    const renderParkingPlacesFlat = () => {
        return (
            <>
                <span>w inwestycji przewidziano:</span>
                {renderParkingPlacesList()}
            </>
        );
    };

    const renderHouseParkingText = () => {
        return (
            <>
                {offer.stats?.ranges_garages_count ? "domy posiadają garaże" : "domy nie posiadają garaży"}{" "}
                {offer.parking_places.length ? renderParkingPlacesHouse(!!offer.stats?.ranges_garages_count) : ""}
            </>
        );
    };

    const renderStandardDescription = () => {
        const hasDescription = () => Object.values(offer.standard_description || {}).filter((field) => isEmpty(field));
        const emptyStandard = <ResponsiveTableRow title="Standard wykończenia:" text="informacja u dewelopera" />;

        return offer.standard_description && hasDescription() ? (
            <OfferStandardDescription
                types={variables != null ? variables.offer_standard_description_type : []}
                standardDescription={offer.standard_description}
            />
        ) : (
            emptyStandard
        );
    };

    const renderHolidayCondoText = (
        <>
            {offer.is_holiday_location ? (
                <a href={rpAppLink.investmentOffer.investmentCategory.base({category: InvestmentOfferTypeSlug.HOLIDAY})} target="_blank">
                    Apartament wakacyjny
                </a>
            ) : (
                ""
            )}{" "}
            {offer.is_condohotel ? (
                <a href={rpAppLink.investmentOffer.investmentCategory.base({category: InvestmentOfferTypeSlug.CONDOHOTEL})} target="_blank">
                    Condohotel
                </a>
            ) : (
                ""
            )}
        </>
    );

    return (
        <DetailSectionWrapper>
            <DetailViewHeader title="Szczegóły inwestycji" />

            <ResponsiveTable>
                <ResponsiveTableRow
                    title="Realizacja inwestycji:"
                    text={
                        futureEndDate || !upper
                            ? `${offer.construction_date_range.lower && `rozpoczęcie budowy w ${lower} roku,`}
                                ${offer.construction_date_range.upper ? `planowane oddanie do użytkowania w ${upper} roku` : "inwestycja w trakcie budowy"}`
                            : `${offer.construction_date_range.lower && `rozpoczęcie budowy w ${lower} roku,`} inwestycja została oddana do użytkowania${
                                  offer.construction_date_range.upper ? ` w ${upper} roku` : ""
                              }`
                    }
                />

                {offer.type === OfferType.FLAT && (
                    <ResponsiveTableRow title="Liczba budynków:" text={`cała inwestycja składa się z ${offer.buildings} ${buildingsPlural(offer.buildings)}`} />
                )}

                {offer.type === OfferType.HOUSE && offer.properties && (
                    <ResponsiveTableRow title="Liczba domów:" text={`cała inwestycja składa się z ${offer.properties} ${housePlural(offer.properties)}`} />
                )}

                {offer.type === OfferType.COMMERCIAL && (
                    <ResponsiveTableRow
                        title="Liczba budynków:"
                        text={`cała inwestycja składa się z ${offer.buildings} ${buildingsCommercialPlural(offer.buildings)}`}
                    />
                )}

                {offer.floors_above_ground_range && floorsAboveLower != null && floorsAboveUpper != null && (
                    <ResponsiveTableRow
                        title="Liczba kondygnacji:"
                        text={
                            offer.type === OfferType.FLAT || offer.type === OfferType.COMMERCIAL
                                ? `${buildingOwnPlural(offer.buildings)} ${
                                      floorsAboveLower === floorsAboveUpper
                                          ? `${floorsAboveUpper} ${floorsAboveGroundPlural(floorsAboveUpper)}`
                                          : `od ${floorsAboveLower} do ${floorsAboveUpper} ${floorsAboveGroundRangePlural(floorsAboveUpper)}`
                                  } wraz z parterem oraz ${
                                      floorsBelowGround
                                          ? `${floorsBelowGround} ${floorsBelowGroundPlural(floorsBelowGround)}`
                                          : `${buildingNotOwnPlural(offer.buildings)} kondygnacji podziemnych`
                                  }`
                                : offer.type === OfferType.HOUSE
                                  ? `domy posiadają ${
                                        floorsAboveLower === floorsAboveUpper
                                            ? `${floorsAboveUpper} ${floorsAboveGroundHousePlural(floorsAboveUpper)}`
                                            : `od ${floorsAboveLower} do ${floorsAboveUpper} kondygnacji`
                                    } wraz z parterem${hasAttic ? " oraz posiadają poddasze" : " oraz nie posiadają poddasza"}`
                                  : ""
                        }
                    />
                )}

                {offer.type === OfferType.FLAT && offer.properties && (
                    <ResponsiveTableRow
                        title={`Liczba ${isApartment || isInvestmentApartment ? "apartamentów" : "mieszkań"}:`}
                        text={`cała inwestycja składa się z ${offer.properties} ${
                            isApartment || isInvestmentApartment ? apartmentsPlural(offer.properties) : localsPlural(offer.properties)
                        }`}
                    />
                )}

                {offer.type === OfferType.COMMERCIAL && offer.properties && (
                    <ResponsiveTableRow
                        title="Liczba lokali:"
                        text={`cała inwestycja składa się z ${offer.properties} ${commercialsPlural(offer.properties)}`}
                    />
                )}

                <ResponsiveTableRow
                    title="Wysokość pomieszczeń:"
                    text={
                        offer.stats?.ranges_height_min
                            ? `${
                                  offer.stats.ranges_height_min === offer.stats.ranges_height_max
                                      ? `na wszystkich kondygnacjach po ${offer.stats.ranges_height_min}`
                                      : `w zależności od kondygnacji od ${offer.stats.ranges_height_min} do ${offer.stats.ranges_height_max}`
                              } cm wysokości`
                            : `informacja u dewelopera`
                    }
                />

                {renderStandardDescription()}

                <ResponsiveTableRow
                    title="Rodzaj własności:"
                    text={
                        offer.stats?.ownership_forms.length
                            ? `${offer.stats.ownership_forms.map((ownership: string) => " " + ownership.toLowerCase())}`
                            : `informacja u dewelopera`
                    }
                />

                {offer.type === OfferType.FLAT && (
                    <>
                        <ResponsiveTableRow
                            title="Powierzchnie dodatkowe:"
                            text={
                                additionalAreasText
                                    ? `${hasEntresol ? "antresola," : ""}${additionalAreasText}`
                                    : `${hasEntresol ? "antresola" : "nie przewidziano powierzchni dodatkowych"}`
                            }
                        />
                        <ResponsiveTableRow
                            title="Komórki lokatorskie:"
                            text={
                                offer.storage
                                    ? `posiada${offer.storage.quantity ? ` (${offer.storage.quantity})` : ""}${
                                          offer.storage.description ? `: ${offer.storage.description}` : ""
                                      }${!!offer.storage.price ? ` Komórki lokatorskie w cenie ${priceFormat(offer.storage.price)}` : ""}`
                                    : `nie przewidziano`
                            }
                        />
                    </>
                )}

                {offer.type === OfferType.HOUSE && <ResponsiveTableRow title="Garaże:" text={renderHouseParkingText()} />}

                {(offer.type === OfferType.FLAT || offer.type === OfferType.COMMERCIAL) && (
                    <ResponsiveTableRow
                        title="Miejsca postojowe:"
                        text={offer.parking_places.length ? renderParkingPlacesFlat() : "w inwestycji nie przewidziano miejsc postojowych"}
                    />
                )}

                {showAmenities && (
                    <ResponsiveTableRow
                        title="Udogodnienia:"
                        text={
                            offer.facilities && offer.facilities.length ? (
                                <OfferDetailFacilities facilities={offer.facilities} facilitiesTypes={variables != null ? variables.offer_facilities : []} />
                            ) : (
                                "informacja u dewelopera"
                            )
                        }
                    />
                )}

                <ResponsiveTableRow
                    title="Przyroda:"
                    text={
                        offer.natural_sites && offer.natural_sites.length ? (
                            <OfferDetailNaturalSites
                                naturalSites={offer.natural_sites}
                                naturalSitesTypes={variables != null ? variables.offer_natural_sites : []}
                            />
                        ) : (
                            `informacja u dewelopera`
                        )
                    }
                />

                {(offer.is_holiday_location || offer.is_condohotel) && <ResponsiveTableRow title="Typ nieruchomości:" text={renderHolidayCondoText} />}
            </ResponsiveTable>
        </DetailSectionWrapper>
    );
};

const parkingPlaceListStyle = css({
    margin: 0,
    py: 0,
    px: 2
});
